import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  BenefitCard,
  CardDescription,
  CardTitle,
  CardsContainer,
  Container,
  IconContainer,
  LearnMoreSection,
  SectionHeading,
  TabButton,
  TabsContainer,
} from './learn_more.styles';

// Simple icon components (kept for category icons if needed)
const BuildingIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l8 3v6c0 5.55-3.84 10.74-9 12-5.16-1.26-9-6.45-9-12V5l8-3z" />
  </svg>
);

const AcademicIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 2L2 7l10 5 10-5-10-5z" />
  </svg>
);

const HeartIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const BriefcaseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 2h4c1.1 0 2 .9 2 2v2h5c1.1 0 2 .9 2 2v11c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2h5V4c0-1.1.9-2 2-2zm2 4V4h-4v2h4z" />
  </svg>
);

// Define the categories and their content
interface CategoryContent {
  id: string;
  label: string;
  icon: React.ComponentType;
  whatWeDo: {
    title: string;
    description: string;
  };
  yourBenefit: {
    title: string;
    description: string;
  };
  endUserBenefit: {
    title: string;
    description: string;
    benefitType: string; // "Student", "Employee", "Senior", "Resident"
  };
}

const CATEGORIES: CategoryContent[] = [
  {
    id: 'property-management',
    label: 'Property management',
    icon: BuildingIcon,
    whatWeDo: {
      title: 'What we do',
      description:
        'Offer your residents smart, on-demand storage with digital inventory and app-based pickups/returns.',
    },
    yourBenefit: {
      title: 'Your benefit',
      description:
        'Residents free up space and manage belongings from anywhere.',
    },
    endUserBenefit: {
      title: 'Resident benefit',
      description:
        'Residents free up space and manage belongings from anywhere.',
      benefitType: 'Resident',
    },
  },
  {
    id: 'higher-education',
    label: 'Higher education',
    icon: AcademicIcon,
    whatWeDo: {
      title: 'What we do',
      description:
        'Simplify move-in/out with managed storage and shipping for students',
    },
    yourBenefit: {
      title: 'Your benefit',
      description:
        'Reduce campus congestion and operational strain during transitions',
    },
    endUserBenefit: {
      title: 'Student benefit',
      description: 'Students get effortless, secure storage between semesters',
      benefitType: 'Student',
    },
  },
  {
    id: 'senior-living',
    label: 'Senior living',
    icon: HeartIcon,
    whatWeDo: {
      title: 'What we do',
      description:
        'Help residents downsize comfortably with seamless moving & storage',
    },
    yourBenefit: {
      title: 'Your benefit',
      description: 'Add value to transitions and enhance move-in experiences',
    },
    endUserBenefit: {
      title: 'Senior benefit',
      description:
        'Residents & families enjoy flexible, stress-free storage options',
      benefitType: 'Senior',
    },
  },
  {
    id: 'employee-benefits',
    label: 'Employee benefits',
    icon: BriefcaseIcon,
    whatWeDo: {
      title: 'What we do',
      description: 'Offer Clutter as a unique lifestyle benefit for employees',
    },
    yourBenefit: {
      title: 'Your benefit',
      description: 'Enhance your benefits program with a zero-cost perk',
    },
    endUserBenefit: {
      title: 'Employee benefit',
      description:
        'Employees access discounted moving and storage services anytime',
      benefitType: 'Employee',
    },
  },
];

export const LearnMoreComponent: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>(
    'property-management',
  );
  const [isCarouselActive, setIsCarouselActive] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const carouselTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-advance carousel
  useEffect(() => {
    if (!isCarouselActive) return;

    const startCarousel = () => {
      // Clear any existing timer
      if (carouselTimerRef.current) {
        clearInterval(carouselTimerRef.current);
      }

      // Start the carousel timer
      carouselTimerRef.current = setInterval(() => {
        setActiveCategory((currentCategory) => {
          const currentIndex = CATEGORIES.findIndex(
            (cat) => cat.id === currentCategory,
          );
          const nextIndex = (currentIndex + 1) % CATEGORIES.length;
          return CATEGORIES[nextIndex].id;
        });
      }, 3000); // Advance every 3 seconds
    };

    startCarousel();

    return () => {
      if (carouselTimerRef.current) {
        clearInterval(carouselTimerRef.current);
        carouselTimerRef.current = null;
      }
    };
  }, [isCarouselActive]);

  // Intersection Observer to detect when section comes into view
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Reset to first tab and start carousel
            setActiveCategory(CATEGORIES[0].id);
            setIsCarouselActive(true);
          } else {
            // Stop carousel when section is out of view
            setIsCarouselActive(false);
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of section is visible
        rootMargin: '0px 0px -20% 0px', // Add some margin to trigger earlier
      },
    );

    observer.observe(section);

    return () => {
      observer.unobserve(section);
      if (carouselTimerRef.current) {
        clearInterval(carouselTimerRef.current);
        carouselTimerRef.current = null;
      }
    };
  }, []);

  // Handle manual tab clicks - pause carousel temporarily
  const handleTabClick = (categoryId: string) => {
    setActiveCategory(categoryId);

    // Pause carousel for a few seconds when user manually clicks
    setIsCarouselActive(false);

    setTimeout(() => {
      setIsCarouselActive(true);
    }, 5000); // Resume after 5 seconds
  };

  const currentCategory =
    CATEGORIES.find((cat) => cat.id === activeCategory) || CATEGORIES[0];

  return (
    <LearnMoreSection ref={sectionRef}>
      <Container>
        <SectionHeading>Learn more about</SectionHeading>

        <TabsContainer role="tablist" aria-label="Learn more categories">
          {CATEGORIES.map((category) => (
            <TabButton
              key={category.id}
              active={activeCategory === category.id}
              onClick={() => handleTabClick(category.id)}
              role="tab"
              aria-selected={activeCategory === category.id}
            >
              {category.label}
            </TabButton>
          ))}
        </TabsContainer>

        <CardsContainer>
          <BenefitCard>
            <IconContainer>
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
              >
                <path
                  d="M0 29.1667V6.66667H6.66667V0H22.5V13.3333H29.1667V29.1667H16.6667V22.5H12.5V29.1667H0ZM2.5 26.6667H6.66667V22.5H2.5V26.6667ZM2.5 20H6.66667V15.8333H2.5V20ZM2.5 13.3333H6.66667V9.16667H2.5V13.3333ZM9.16667 20H13.3333V15.8333H9.16667V20ZM9.16667 13.3333H13.3333V9.16667H9.16667V13.3333ZM9.16667 6.66667H13.3333V2.5H9.16667V6.66667ZM15.8333 20H20V15.8333H15.8333V20ZM15.8333 13.3333H20V9.16667H15.8333V13.3333ZM15.8333 6.66667H20V2.5H15.8333V6.66667ZM22.5 26.6667H26.6667V22.5H22.5V26.6667ZM22.5 20H26.6667V15.8333H22.5V20Z"
                  fill="#097E77"
                />
              </svg>
            </IconContainer>
            <CardTitle>{currentCategory.whatWeDo.title}</CardTitle>
            <CardDescription>
              {currentCategory.whatWeDo.description}
            </CardDescription>
          </BenefitCard>

          <BenefitCard>
            <IconContainer>
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
              >
                <mask
                  id="mask0_28_273"
                  style={{ maskType: 'alpha' }}
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="40"
                  height="40"
                >
                  <rect width="40" height="40" fill="#D9D9D9" />
                </mask>
                <g mask="url(#mask0_28_273)">
                  <path
                    d="M19.6475 33.2855C19.8119 33.2855 19.9792 33.247 20.1492 33.17C20.3189 33.0931 20.4529 33.0055 20.5512 32.9071L33.8812 19.5771C34.2574 19.201 34.5405 18.7992 34.7308 18.3717C34.9208 17.9445 35.0158 17.4959 35.0158 17.0259C35.0158 16.5387 34.9208 16.0696 34.7308 15.6188C34.5405 15.1677 34.2574 14.7627 33.8812 14.4038L27.2146 7.73713C26.8557 7.36102 26.4668 7.08588 26.0479 6.91171C25.6293 6.73782 25.1764 6.65088 24.6892 6.65088C24.2192 6.65088 23.7678 6.73782 23.335 6.91171C22.9022 7.08588 22.5032 7.36102 22.1379 7.73713L21.1825 8.69255L24.2658 11.8013C24.6397 12.1582 24.916 12.5653 25.0946 13.0225C25.2729 13.4798 25.3621 13.9541 25.3621 14.4455C25.3621 15.4627 25.0224 16.311 24.3429 16.9905C23.6635 17.6699 22.8151 18.0096 21.7979 18.0096C21.3065 18.0096 20.8305 17.9284 20.37 17.7659C19.9097 17.6037 19.5011 17.3441 19.1442 16.9871L15.9871 13.8559L8.74374 21.0992C8.61763 21.2253 8.52304 21.3664 8.45999 21.5225C8.39693 21.6784 8.36541 21.8387 8.36541 22.0034C8.36541 22.3109 8.47013 22.5737 8.67957 22.7917C8.88902 23.0098 9.14749 23.1188 9.45499 23.1188C9.61971 23.1188 9.78693 23.0803 9.95666 23.0034C10.1267 22.9264 10.2608 22.8388 10.3592 22.7405L15.8333 17.2663L17.5896 19.0225L12.1408 24.4967C12.015 24.6228 11.9205 24.7639 11.8575 24.92C11.7944 25.0759 11.7629 25.2362 11.7629 25.4009C11.7629 25.6978 11.8703 25.9537 12.085 26.1684C12.2997 26.3831 12.5555 26.4905 12.8525 26.4905C13.0172 26.4905 13.1844 26.452 13.3542 26.375C13.5242 26.2981 13.6582 26.2105 13.7562 26.1121L19.4229 20.4713L21.1796 22.2275L15.5383 27.8942C15.423 27.9925 15.3312 28.1266 15.2629 28.2963C15.1946 28.4663 15.1604 28.6335 15.1604 28.798C15.1604 29.0952 15.2678 29.3512 15.4825 29.5659C15.6972 29.7806 15.953 29.888 16.25 29.888C16.4144 29.888 16.5747 29.8564 16.7308 29.7934C16.8867 29.7303 17.0276 29.6357 17.1537 29.5096L22.8204 23.8688L24.5771 25.625L18.9104 31.2917C18.7843 31.4178 18.6897 31.5642 18.6267 31.7309C18.5636 31.8975 18.5321 32.0578 18.5321 32.2117C18.5321 32.5192 18.6464 32.775 18.875 32.9792C19.1036 33.1834 19.3611 33.2855 19.6475 33.2855ZM19.6217 35.785C18.6794 35.785 17.8579 35.4582 17.1571 34.8046C16.4562 34.1507 16.0899 33.3366 16.0579 32.3621C15.1135 32.298 14.3244 31.9625 13.6908 31.3559C13.0572 30.7489 12.7137 29.9519 12.6604 28.9646C11.6732 28.9007 10.8751 28.5557 10.2662 27.9296C9.65707 27.3035 9.3311 26.516 9.28832 25.5671C8.29693 25.5032 7.4786 25.1438 6.83332 24.4888C6.18804 23.8338 5.86541 23.0053 5.86541 22.0034C5.86541 21.512 5.95888 21.0306 6.14582 20.5592C6.33277 20.0881 6.60471 19.6742 6.96166 19.3175L15.9871 10.3175L20.8687 15.1988C20.9668 15.3141 21.0954 15.406 21.2546 15.4746C21.414 15.543 21.5867 15.5771 21.7725 15.5771C22.0758 15.5771 22.3375 15.4767 22.5575 15.2759C22.7778 15.075 22.8879 14.8121 22.8879 14.4871C22.8879 14.3013 22.8537 14.1288 22.7854 13.9696C22.7168 13.8105 22.6248 13.6817 22.5096 13.5834L16.6633 7.73713C16.3044 7.36102 15.9129 7.08588 15.4887 6.91171C15.0646 6.73782 14.6089 6.65088 14.1217 6.65088C13.6517 6.65088 13.2057 6.73782 12.7837 6.91171C12.3615 7.08588 11.9625 7.36102 11.5867 7.73713L6.11207 13.2371C5.80874 13.5405 5.56041 13.8989 5.36707 14.3125C5.17374 14.7259 5.05999 15.1474 5.02582 15.5771C4.99138 15.9319 5.00735 16.2828 5.07374 16.63C5.13985 16.9773 5.25624 17.3037 5.42291 17.6092L3.58332 19.4488C3.20721 18.906 2.92416 18.2981 2.73416 17.625C2.54388 16.952 2.46582 16.2694 2.49999 15.5771C2.53416 14.8099 2.70721 14.0689 3.01916 13.3542C3.3311 12.6395 3.76804 12.0012 4.32999 11.4392L9.78832 5.98088C10.4125 5.37393 11.0915 4.91727 11.8254 4.61088C12.5593 4.30421 13.3301 4.15088 14.1379 4.15088C14.9454 4.15088 15.7135 4.30421 16.4421 4.61088C17.171 4.91727 17.8387 5.37393 18.4454 5.98088L19.4008 6.93588L20.3558 5.98088C20.9797 5.37393 21.656 4.91727 22.3846 4.61088C23.1132 4.30421 23.8814 4.15088 24.6892 4.15088C25.4969 4.15088 26.2678 4.30421 27.0017 4.61088C27.7355 4.91727 28.406 5.37393 29.0129 5.98088L35.6379 12.6059C36.2446 13.2128 36.7093 13.9035 37.0321 14.678C37.3546 15.4524 37.5158 16.2435 37.5158 17.0513C37.5158 17.8591 37.3546 18.6273 37.0321 19.3559C36.7093 20.0845 36.2446 20.7521 35.6379 21.3588L22.3075 34.6634C21.94 35.0309 21.5261 35.3098 21.0658 35.5C20.6053 35.69 20.1239 35.785 19.6217 35.785Z"
                    fill="#097E77"
                  />
                </g>
              </svg>
            </IconContainer>
            <CardTitle>{currentCategory.yourBenefit.title}</CardTitle>
            <CardDescription>
              {currentCategory.yourBenefit.description}
            </CardDescription>
          </BenefitCard>

          <BenefitCard>
            <IconContainer>
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="33"
                height="33"
                viewBox="0 0 33 33"
                fill="none"
              >
                <path
                  d="M23.5737 16.9871L16.9775 10.6058C16.1806 9.83667 15.5101 8.98042 14.9663 8.03708C14.4224 7.09375 14.1504 6.06639 14.1504 4.955C14.1504 3.57861 14.6322 2.40875 15.5958 1.44542C16.5592 0.481806 17.729 0 19.1054 0C20.0051 0 20.8358 0.216944 21.5975 0.650833C22.3594 1.08444 23.0182 1.63681 23.5737 2.30792C24.1293 1.63681 24.7879 1.08444 25.5496 0.650833C26.3112 0.216944 27.1419 0 28.0417 0C29.4181 0 30.5879 0.481806 31.5512 1.44542C32.5149 2.40875 32.9967 3.57861 32.9967 4.955C32.9967 6.06639 32.729 7.09375 32.1937 8.03708C31.6585 8.98042 30.9924 9.83667 30.1954 10.6058L23.5737 16.9871ZM23.5737 13.5067L28.3396 8.88792C28.8993 8.33875 29.3987 7.74153 29.8379 7.09625C30.2771 6.45097 30.4967 5.73722 30.4967 4.955C30.4967 4.26917 30.2589 3.68861 29.7833 3.21333C29.3081 2.73778 28.7275 2.5 28.0417 2.5C27.5139 2.5 27.031 2.63778 26.5929 2.91333C26.1549 3.18917 25.7681 3.53861 25.4325 3.96167L23.5737 6.22417L21.7146 3.96167C21.379 3.53861 20.9922 3.18917 20.5542 2.91333C20.1164 2.63778 19.6335 2.5 19.1054 2.5C18.4196 2.5 17.839 2.73778 17.3638 3.21333C16.8882 3.68861 16.6504 4.26917 16.6504 4.955C16.6504 5.73722 16.87 6.45097 17.3092 7.09625C17.7483 7.74153 18.2478 8.33875 18.8075 8.88792L23.5737 13.5067ZM8.33333 26.795L20.4292 30.2179L30.3779 27.1346C30.2926 26.7565 30.1164 26.4665 29.8492 26.2646C29.5819 26.0626 29.2796 25.9617 28.9421 25.9617H20.78C20.0528 25.9617 19.4349 25.9339 18.9263 25.8783C18.4176 25.8228 17.8951 25.701 17.3587 25.5129L13.5962 24.2692L14.3363 21.8204L17.7113 22.9937C18.2157 23.1646 18.7981 23.281 19.4583 23.3429C20.1186 23.4049 21.0576 23.4444 22.2754 23.4617C22.2754 23.0492 22.1825 22.6933 21.9967 22.3942C21.8108 22.095 21.5661 21.8931 21.2625 21.7883L11.5929 18.2371C11.5607 18.2265 11.5312 18.2186 11.5046 18.2133C11.4779 18.2078 11.4486 18.205 11.4167 18.205H8.33333V26.795ZM0 31.6667V15.7054H11.3975C11.5725 15.7054 11.7497 15.7246 11.9292 15.7629C12.1089 15.8015 12.2756 15.8464 12.4292 15.8975L22.1408 19.4746C22.8972 19.7543 23.5254 20.2494 24.0254 20.96C24.5254 21.6706 24.7754 22.5044 24.7754 23.4617H28.9421C30.1387 23.4617 31.1136 23.8479 31.8667 24.6204C32.62 25.3926 32.9967 26.3953 32.9967 27.6283V28.9742L20.5125 32.8525L8.33333 29.3783V31.6667H0ZM2.5 29.1667H5.83333V18.205H2.5V29.1667Z"
                  fill="#097E77"
                />
              </svg>
            </IconContainer>
            <CardTitle>{currentCategory.endUserBenefit.title}</CardTitle>
            <CardDescription>
              {currentCategory.endUserBenefit.description}
            </CardDescription>
          </BenefitCard>
        </CardsContainer>
      </Container>
    </LearnMoreSection>
  );
};
